<template>
	<AppCard alpha no-header inset>
		<template #body>
			<AppList class="printers-list" :items="settings">
				<template #sn-slot="{item}">
					<div class="row centered space-between">
						<p>{{item.title}}</p>
						<div class="serial-number-container" @click="copySerialNumber(item.value)" @mouseenter="hoveredSerial = true" @mouseleave="hoveredSerial = false">
							<p class="serial-number-value">{{ item.value }}</p>
							<AppInPlaceCloud :active="hoveredSerial" force-top>
								<div class="centered pl-20 pr-20">
									<p>{{ serialCopyMessage }}</p>
								</div>
							</AppInPlaceCloud>
						</div>
					</div>
				</template>
				<template #checklist-slot="{item}">
					<div class="row centered space-between checklist-container">
						<p class="checklist-p">{{ getString('category_management') }}</p>
						<AppButton class="input-item" small outline @click="openBottomSheetCategorySettings = true" :text="getString(item.title)" />
					</div>
				</template>
				<template #checklist-station-slot="{item}">
					<div class="row centered space-between checklist-container">
						<p class="checklist-p">{{item.title}}</p>
						<AppChecklist
							:title="item.title"
							:values="stationsTitles"
							:selected-indexes="selectedStationsIndexes"
							class="input-item"
							@change-checked="checkStation"
						/>
					</div>
				</template>
				<template #input-slot="{item}">
					<div class="row centered space-between">
						<p class="input-p">{{item.title}}</p>
						<AppTextInput
							:value="item.value"
							:placeholder="item.title"
							@changed-text="item.callback"
							class="input-item"
						/>
					</div>
				</template>
				<template #btn-slot="{item}">
					<div class="row centered space-between">
						<p class="button-p">{{item.title}}</p>
						<AppButton class="input-item" small :outline="item.buttonType === 'outline'" @click="item.callback" :text="getString(item.buttonName)" :loading="!!item.loading" />
					</div>
				</template>
				<template #dropdown-options-slot="{item}">
					<div class="deep-level-three">
						<AppList :items="item.options" >
							<template #dropdown-options-slot="{item}">
								<AppList :items="item.options" />
							</template>
						</AppList>
					</div>
				</template>
			</AppList>
		</template>
	</AppCard>
	<div class="row centered space-between mt-10">
		<div class="mr-8 left-side">
			<AppButton :text="getString('duplicate')" outline @click="toggleDuplicatePopUp"/>
		</div>
		<div class="right-side">
			<AppButton class="mr-8" @click="$refs['popupWarnDelete'].toggleStatus(true)" :text="getString('delete')" outline />
			<AppButton @click="clickSavePrinter" :text="getString('save')" />
		</div>
	</div>
	<AppPopup
		ref="popupWarnDelete"
		:title="getString('warning')"
		:body-text="getString('do_you_want_to_delete_printer')"
		:buttons="[{text: getString('cancel'), negative: true}, {text: getString('continue'), callback: clickDeletePrinter, loading: loadingDelete}]" />
	<AppBottomSheet :force-width="bottomSheetWidth" :active="openBottomSheetCategorySettings" @toggling-popup="toggleCategorySettings">
		<div class="new-room-container" v-if="openBottomSheetCategorySettings">
			<PrinterCategorySettingEditor :printer-id="printerId" @close="openBottomSheetCategorySettings = false" />
		</div>
	</AppBottomSheet>
	<AppPopup
		ref="popupWarnDuplicate"
		:title="getString('do_you_want_clone_printer')"
		:buttons="[
			{text: getString('cancel'), negative: true},
			{text: getString('duplicate'), callback: clickDuplicatePrinter, loading: loadingDuplicate}
		]"/>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppList from "@/shared/components/AppList.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppSelect from "@/shared/components/AppSelect.vue";

import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, usePrintersStore, useUserStore} from "@/console-new/stores";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppInPlaceCloud from "@/shared/components/AppInPlaceCloud.vue";
import PrinterCategorySettingEditor from "@/console-new/components/config/printers/PrinterCategorySettingsEditor.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let userStore = useUserStore();
		let printersStore = usePrintersStore();
		let notificationStore = useNotificationStore();
		return {getString: languageStore.getString, showNotification: notificationStore.showNotification, menuStore, printersStore, userStore};
	},
	name: "PrinterSettings",
	components: {AppBottomSheet, AppPopup, AppTextInput, AppCard, AppButton, AppChecklist, AppChevronIcon, AppList, AppInPlaceCloud, PrinterCategorySettingEditor, AppSelect},
	mixins: [WindowWidth],
	props: {
		printerId: {
			type: Number,
			required: true,
		},
	},
	emits: ['printer-duplicated'],
	data() {
		return {
			activeOptionsDropdown: false,
			activeFeaturesDropdown: false,
			activeEnableDropdown: false,
			activeChevronMode: false,
			activeChevronBusiness: false,
			activeChevronCustomer: false,
			loadingDelete: false,
			loadingTestPrint: false,
			openBottomSheetCategorySettings: false,
			loadingDuplicate: false,
			hoveredSerial: false,
			serialCopyMessage: this.getString('copy'),
		};
	},
	computed: {
		printer() {
			return this.printersStore.businessPrinters ? this.printersStore.businessPrinters.find(printer => printer.id === this.printerId) : {};
		},
		businessStations() {
			if(this.userStore.businessUsers) {
				return Object.values(this.userStore.businessUsers).filter(user => user.station);
			}
			return [];
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		stationsTitles() {
			let returningNames = [this.getString("online")];
			if(this.businessStations) {
				this.businessStations.forEach(station => {
					returningNames.push(station.name);
				});
			}
			return returningNames;
		},
		selectedStationsIndexes() {
			let returningIndexes = [];
			if(!this.printer.usersExcl.includes(null)) {
				returningIndexes.push(0);
			}
			for(let i = 0; i < this.businessStations.length; i++) {
				if(!this.printer.usersExcl.includes(this.businessStations[i].user_id)) {
					returningIndexes.push(i+1);
				}
			}
			return returningIndexes;
		},
		settings() {
			let nameItem = {
				title: this.getString('printer_name_placeholder'),
				customSlot: 'input-slot',
				value: this.printer.name,
				callback: v=>{ this.printer.name = v }
			};
			let activeItem = {
				title: this.getString('print_modes'),
				chevronItem: true,
				dropdownSlot: 'dropdown-options-slot',
				active: this.activeChevronMode,
				callback: v=>{
					this.activeChevronMode = v;
					this.activeChevronBusiness = false;
					this.activeChevronCustomer = false;
					this.activeOptionsDropdown = false;
					this.activeFeaturesDropdown = false;
					this.activeEnableDropdown = false;
				},
				options: [
					{
						title: this.getString('active_print'),
						switch: true,
						active: this.printer.active,
						callback: v=>{ this.printer.active = v },
					},
					{
						title: this.getString('business_created_orders'),
						chevronItem: true,
						dropdownSlot: 'dropdown-options-slot',
						active: this.activeChevronBusiness,
						callback: v=>{
							this.activeChevronBusiness = v;
							this.activeChevronCustomer = false;
						},
						options: [
							{
								title: this.getString('counter'),
								switch: true,
								active: this.printer.mode_counter_business,
								callback: v=>{ this.printer.mode_counter_business = v },
							},
							{
								title: this.getString('qr_code_tables'),
								switch: true,
								active: this.printer.mode_qr_business,
								callback: v=>{ this.printer.mode_qr_business = v },
							},
							{
								title: this.getString('delivery'),
								switch: true,
								active: this.printer.mode_delivery_business,
								callback: v=>{ this.printer.mode_delivery_business = v },
							},
							{
								title: this.getString('takeaway'),
								switch: true,
								active: this.printer.mode_takeaway_business,
								callback: v=>{ this.printer.mode_takeaway_business = v },
							}
						]
					},
					{
						title: this.getString('customer_created_orders'),
						chevronItem: true,
						dropdownSlot: 'dropdown-options-slot',
						active: this.activeChevronCustomer,
						callback: v=>{
							this.activeChevronCustomer = v;
							this.activeChevronBusiness = false;
						},
						options: [
							{
								title: this.getString('qr_code_tables'),
								switch: true,
								active: this.printer.mode_qr_customer,
								callback: v=>{ this.printer.mode_qr_customer = v },
							},
							{
								title: this.getString('delivery'),
								switch: true,
								active: this.printer.mode_delivery_customer,
								callback: v=>{ this.printer.mode_delivery_customer = v },
							},
							{
								title: this.getString('takeaway'),
								switch: true,
								active: this.printer.mode_takeaway_customer,
								callback: v=>{ this.printer.mode_takeaway_customer = v },
							}
						]
					},
				],
			};
			let categoriesItem = {
				title: this.getString('categories'),
				customSlot: 'checklist-slot',
			};
			let stationsItem = {
				title: this.getString('stations'),
				customSlot: 'checklist-station-slot',
			};
			let testPrintItem = {
				title: this.getString('test_print_info'),
				buttonName: this.getString('test_print'),
				buttonType: 'outline',
				customSlot: 'btn-slot',
				loading: this.loadingTestPrint,
				callback: this.clickTestPrint,
			};
			let defaultOptions = [
				{title: this.getString('print_order_total'), active: this.printer.print_order_total, switch: true, callback: v=>{ this.printer.print_order_total = v }},
				{title: this.getString('truncate_long_names'), active: this.printer.name_break_line, switch: true, callback: v=>{ this.printer.name_break_line = v }},
				{title: this.getString('double_height'), active: this.printer.product_double_height, switch: true, callback: v=>{ this.printer.product_double_height = v }},
				{title: this.getString('white_space_header'), active: this.printer.white_space_header, switch: true, callback: v=>{ this.printer.white_space_header = v }},
				{title: this.getString('left_side_quantities'), active: this.printer.left_side_quantities, switch: true, callback: v=>{ this.printer.left_side_quantities = v }},
				{title: this.getString('header_double_size'), active: this.printer.header_double_size, switch: true, callback: v=>{ this.printer.header_double_size = v }},
				{title: this.getString('current_course_big'), active: this.printer.current_course_big, switch: true, callback: v=>{ this.printer.current_course_big = v }},
				{title: this.getString('print_staff_name'), active: this.printer.print_staff_name, switch: true, callback: v=>{ this.printer.print_staff_name = v }},
			{title: this.getString('print_paid_status'), active: this.printer.print_paid_status, switch: true, callback: v=>{ this.printer.print_paid_status = v }},
			];
			let featuresOptions = [
				{title: this.getString('edit_order_summary'), active: this.printer.edit_order_summary, switch: true, callback: v=>{ this.printer.edit_order_summary = v }},
				{title: this.getString('print_redeem_qr'), active: this.printer.print_redeem_qr, switch: true, callback: v=>{ this.printer.print_redeem_qr = v }},
				{title: this.getString('print_order_qr'), active: this.printer.print_order_qr, switch: true, callback: v=>{ this.printer.print_order_qr = v }},
				{title: this.getString('print_exploded_items'), active: this.printer.print_exploded_items, switch: true, callback: v=>{ this.printer.print_exploded_items = v }},
			];
			if(this.printer.server_version > 3) {
				featuresOptions.push({title: this.getString('play_print_audio'), active: this.printer.play_audio, switch: true, callback: v=>{ this.printer.play_audio = v }});
			}
			let enableOptions = [
				{title: this.getString('fiscal_print'), active: this.printer.fiscal_print, switch: true, callback: v=>{ this.printer.fiscal_print = v }},
				{title: this.getString('enable_reprint'), active: this.printer.can_reprint_orders, switch: true, callback: v=>{ this.printer.can_reprint_orders = v }},
				{title: this.getString('enable_prebill'), active: this.printer.can_print_prebill, switch: true, callback: v=>{ this.printer.can_print_prebill = v }},
				{title: this.getString('enable_posreceipt'), active: this.printer.can_print_posreceipt, switch: true, callback: v=>{ this.printer.can_print_posreceipt = v }},
			];
			if(this.userStore.currBusiness?.active_plan === 'allinone' || this.userStore.currBusiness?.active_plan === 'enterprise') {
				enableOptions.push({title: this.getString('enable_reservations'), active: this.printer.can_print_reservation, switch: true, callback: v=>{ this.printer.can_print_reservation = v }});
			}
			enableOptions.push({title: this.getString('enable_customer_credit'), active: this.printer.can_print_customer_credit, switch: true, callback: v=>{ this.printer.can_print_customer_credit = v }});
			let optionsDesignItem = {
				title: this.getString('graphics_options'),
				chevronItem: true,
				dropdownSlot: 'dropdown-options-slot',
				active: this.activeOptionsDropdown,
				callback: v=>{
					this.activeOptionsDropdown = v;
					this.activeFeaturesDropdown = false;
					this.activeEnableDropdown = false;
					this.activeChevronMode = false;
				},
				options: defaultOptions
			};
			let optionsFeaturesItem = {
				title: this.getString('features_options'),
				chevronItem: true,
				dropdownSlot: 'dropdown-options-slot',
				active: this.activeFeaturesDropdown,
				callback: v=>{
					this.activeFeaturesDropdown = v;
					this.activeOptionsDropdown = false;
					this.activeEnableDropdown = false;
					this.activeChevronMode = false;
				},
				options: featuresOptions
			};
			let optionsEnableItem = {
				title: this.getString('enable_options'),
				chevronItem: true,
				dropdownSlot: 'dropdown-options-slot',
				active: this.activeEnableDropdown,
				callback: v=>{
					this.activeEnableDropdown = v;
					this.activeOptionsDropdown = false;
					this.activeFeaturesDropdown = false;
					this.activeChevronMode = false;
				},
				options: enableOptions
			};
			let snItem = {
				title: this.getString('serial_number'),
				customSlot: 'sn-slot',
				value: this.printer.printer_id,
			};
			return [nameItem, snItem, activeItem, optionsEnableItem, optionsDesignItem, optionsFeaturesItem, categoriesItem, stationsItem, testPrintItem];
		},
	},
	methods: {
		copySerialNumber(serialNumber) {
			navigator.clipboard.writeText(serialNumber);
			this.serialCopyMessage = this.getString('copied');
			setTimeout(() => {
				this.serialCopyMessage = this.getString('copy');
			}, 1500);
		},
		clickTestPrint() {
			this.loadingTestPrint = true;
			this.printersStore.testPrint(this.printer.id, success=>{
				this.loadingTestPrint = false;
				this.showNotification(success ? 'test-print-success' : 'generic-fail');
			});
		},
		clickSavePrinter() {
			this.printersStore.savePrinter(this.printer.id, success=>{
				if(success) {
					this.printersStore.updatePrinterExcludedCategories(this.printer.id, this.printer.categoriesExcl, this.printer.categoriesSmall, this.printer.usersExcl, catSuccess => {
						this.showNotification(catSuccess ? 'generic-success' : 'generic-fail');
					});
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		clickDeletePrinter() {
			this.loadingDelete = true;
			this.printersStore.deletePrinter(this.printer.id, success=>{
				this.loadingDelete = false;
				this.$refs['popupWarnDelete'].toggleStatus(false);
				if(success) {
					this.showNotification('generic-success');
					this.printersStore.businessPrinters = null;
					this.printersStore.getBusinessPrinters(() => {
						this.showNotification("generic-success");
					});
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		checkStation(newCheckedIndexes) {
			let newStationExcl = [];
			if(!newCheckedIndexes.includes(0)) {
				newStationExcl.push(null);
			}
			Object.keys(this.businessStations).forEach((stationId, index) => {
				if(!newCheckedIndexes.includes(index+1)) {
					newStationExcl.push(this.businessStations[stationId].user_id);
				}
			});
			this.printer.usersExcl = newStationExcl;
		},
		toggleCategorySettings(isOpen) {
			if(!isOpen) {
				this.openBottomSheetCategorySettings = false;
			}
		},
		toggleDuplicatePopUp() {
			this.$refs['popupWarnDuplicate'].toggleStatus(true);
		},
		clickDuplicatePrinter() {
			this.loadingDuplicate = true;
			this.printersStore.clonePrinter(this.printer.id, success => {
				this.loadingDelete = false;
				this.$refs['popupWarnDuplicate'].toggleStatus(false);
				if(success.result === "OK") {
					this.$emit('printer-duplicated', parseInt(success.newPrinterId));
					this.showNotification('generic-success');
					this.printersStore.businessPrinters = null;
					this.printersStore.getBusinessPrinters(() => {
						this.showNotification("generic-success");
						this.loadingDuplicate = false;
					});
				} else {
					this.showNotification('generic-fail');
				}
			});
		}
	},
	created() {
		if(!this.menuStore.loadedBusinessMenu && this.menuStore.currBusinessId !== null) {
			this.menuStore.loadMenu();
		}
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.menuStore.loadMenu();
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.input-p, .checklist-p {
	width: calc(100% - 280px);
	@media (max-width: 600px) {
		width: calc(100% - 210px);
	}
	@media (max-width: 450px) {
		width: calc(100% - 170px);
	}
}
.checklist-p {
	width: calc(100% - 260px);
}
.chevron-p {
	width: calc(100% - 50px);
}
.button-p {
	width: calc(100% - 120px);
}
.input-item {
	&.select-wrapper, &.input-wrapper {
		@media (max-width: 600px) {
			max-width: 200px;
		}
		@media (max-width: 450px) {
			max-width: 160px;
		}
	}
	@media (max-width: 350px) {
		margin-left: auto;
	}
}
.checklist-p, .chevron-p, .button-p, .input-p {
	@media (max-width: 600px) {
		font-size: 14px;
	}
	@media (max-width: 400px) {
		font-size: 12px;
	}
}
.checklist-p, .button-p, .input-p {
	@media (max-width: 350px) {
		width: 100%;
		margin-bottom: 6px;
	}
}
.row-clickable {
	cursor: pointer;

	.chevron-container {
		height: 35px;
		display: flex;
		align-items: center;
	}
}

.printers-list:deep(.dropdown-container) {
	 .dropdown .dropdown-content {
		 background-color: var(--color-bg-primary-2);
		 box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1882352941);
		 padding: 0 10px;

		 .list-item {
			 padding-left: 0;
			 padding-right: 0;
		 }
	}
}
.checklist-container:deep(.dropdown-container) {
	.dropdown .dropdown-content {
		border-radius: 0 0 13px 13px;
		box-shadow: unset;
		padding: 0;

	}
}
.deep-level-three:deep(.dropdown-container) {
	.dropdown .dropdown-content {
		background-color: var(--color-alpha-1);
		box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.1882352941);
	}
}

.serial-number-container {
	position: relative;
	cursor: pointer;
}
.serial-number-value {
	color: #9B9B9B;
	font-size: 14px;
	user-select: none;
}
.right-side {
	display: flex;
	flex-wrap: wrap;
	flex-direction: row;
}
.left-side {
	@media(max-width: 398px) {
		margin-bottom: 8px;
	}
}
</style>